import React from "react";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";
import { useBookingGate } from "../../lib/bookingGate";

/**
 * FindOnCampsitesCoUk — "Find on Campsites.co.uk" button.
 *
 * Why this exists vs just a regular link:
 *   Campsites.co.uk has no URL-parameter search (their `?q=` is ignored by
 *   their JS-driven search). They also have no public name lookup API.
 *   So a "Book" button is dishonest. Instead we send the user to the
 *   right COUNTY page (e.g. /search/campsites-in-cornwall) which is a
 *   real, indexed page on their site, and COPY the campsite name to the
 *   user's clipboard so they can paste-search once they land. The toast
 *   confirms the copy so they know what to do.
 *
 *   Result: 2-tap booking flow (open → paste-search → click site → book)
 *   that's honest about the slight extra friction.
 *
 * Gated by useBookingGate so anonymous users are bounced to /register
 * before they leave the site.
 */
export default function FindOnCampsitesCoUk({ url, campsiteName, fullWidth = true, testId = "find-campsites-co-uk" }) {
  const gate = useBookingGate();

  if (!url) return null;

  const handleClick = (e) => {
    // Auth gate first — if logged out, this preventDefaults the click
    gate(e, { reason: "find this campsite on Campsites.co.uk" });
    if (e.defaultPrevented) return;

    // Copy the campsite name to the clipboard so the user can paste-search
    // once they land on the county page. Clipboard API requires a user
    // gesture, which a click handler provides.
    if (campsiteName) {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(campsiteName).then(
            () => toast.success(`"${campsiteName}" copied — paste into the Campsites.co.uk search box`, { duration: 5000 }),
            () => toast.info(`Search Campsites.co.uk for: ${campsiteName}`, { duration: 5000 }),
          );
        } else {
          // Fallback for browsers without Clipboard API (older Safari)
          toast.info(`Search Campsites.co.uk for: ${campsiteName}`, { duration: 5000 });
        }
      } catch {
        toast.info(`Search Campsites.co.uk for: ${campsiteName}`, { duration: 5000 });
      }
    }
    // Let the link's default behaviour (new tab) proceed
  };

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      className={`inline-flex items-center justify-center ${fullWidth ? "w-full" : ""} px-4 py-4 bg-[#7c3aed] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#6d28d9] active:translate-y-[1px] transition-colors gap-2 shadow-[0_4px_0_0_#4c1d95,0_0_0_3px_rgba(124,58,237,0.18)]`}
      data-testid={testId}
      title="Opens the matching county page on Campsites.co.uk; campsite name copied to your clipboard for one-paste search."
    >
      <ExternalLink className="w-4 h-4 shrink-0" strokeWidth={2.25} />
      Find on Campsites.co.uk
    </a>
  );
}
